import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const docApi = {
    list: (vendorProfileId, q = "") =>
        axiosInstance.get(`/api/superadmin/vendors/${vendorProfileId}/documents/?q=${encodeURIComponent(q)}`),
    get: (vendorProfileId, docId) =>
        axiosInstance.get(`/api/superadmin/vendors/${vendorProfileId}/documents/${docId}/`),
    create: (vendorProfileId, formData) =>
        axiosInstance.post(`/api/superadmin/vendors/${vendorProfileId}/documents/`, formData),
    patch: (vendorProfileId, docId, body) =>
        axiosInstance.patch(`/api/superadmin/vendors/${vendorProfileId}/documents/${docId}/`, body),
    delete: (vendorProfileId, docId) =>
        axiosInstance.delete(`/api/superadmin/vendors/${vendorProfileId}/documents/${docId}/`),
};

const DOC_TYPES = [
    { value: "gstin", label: "GSTIN", bg: "#dcfce7", color: "#16a34a", border: "#bbf7d0" },
    { value: "pan",   label: "PAN",   bg: "#dbeafe", color: "#1d4ed8", border: "#bfdbfe" },
    { value: "coi",   label: "COI",   bg: "#fef3c7", color: "#d97706", border: "#fde68a" },
    { value: "msme",  label: "MSME",  bg: "#ede9fe", color: "#7c3aed", border: "#ddd6fe" },
];

const getDocCfg = (type) => DOC_TYPES.find((d) => d.value === type) || { label: "DOC", bg: "#f1f5f9", color: "#475569", border: "#e2e8f0", emoji: "📄" };

const formatBytes = (b) => {
    if (!b) return "—";
    return b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.round(b / 1024)} KB`;
};

const getFileKind = (doc) => {
    if (doc.content_type?.startsWith("image/")) return "image";
    if (
        doc.content_type?.includes("pdf") ||
        doc.original_name?.toLowerCase().endsWith(".pdf")
    ) return "pdf";
    if (
        doc.content_type?.includes("spreadsheet") ||
        doc.content_type?.includes("excel") ||
        doc.original_name?.match(/\.(xlsx?|csv)$/i)
    ) return "spreadsheet";
    if (
        doc.content_type?.includes("word") ||
        doc.original_name?.match(/\.(docx?)$/i)
    ) return "word";
    return "other";
};

const FILE_KIND_META = {
    image:       { icon: "🖼️", label: "Image",       bg: "#0f172a" },
    pdf:         { icon: "📄", label: "PDF Document", bg: "#fef2f2", color: "#dc2626" },
    spreadsheet: { icon: "📊", label: "Spreadsheet",  bg: "#f0fdf4", color: "#16a34a" },
    word:        { icon: "📝", label: "Word Document", bg: "#eff6ff", color: "#2563eb" },
    other:       { icon: "📁", label: "Document",     bg: "#f8fafc", color: "#64748b" },
};

const downloadFile = async (url, filename) => {
    try {
        const resp = await fetch(url, { mode: "cors" });
        if (!resp.ok) throw new Error("fetch failed");
        const blob = await resp.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = filename || "document";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch {
      
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "document";
        a.target = "_blank";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }
};

const TOAST_MS = 4000;
let _vdSetter = null;
const toast = {
    _push: (item) => { const id = Date.now() + Math.random(); _vdSetter?.((p) => [...p, { id, ...item }]); return id; },
    success: (t, m) => toast._push({ type: "success", title: t, msg: m }),
    error:   (t, m) => toast._push({ type: "error",   title: t, msg: m }),
    info:    (t, m) => toast._push({ type: "info",    title: t, msg: m }),
    loading: (t)    => toast._push({ type: "loading", title: t, persistent: true }),
    dismiss: (id)   => _vdSetter?.((p) => p.filter((x) => x.id !== id)),
};
const TMETA = {
    success: { bar: "#16a34a", icon: "✓" },
    error:   { bar: "#dc2626", icon: "✕" },
    info:    { bar: "#1d4ed8", icon: "i" },
    loading: { bar: "#94a3b8" },
};
const TItem = ({ t, onDismiss }) => {
    const m = TMETA[t.type];
    const [prog, setProg] = useState(100);
    const [exit, setExit] = useState(false);
    const raf = useRef(null);
    const ts  = useRef(Date.now());
    const dismiss = useCallback(() => { setExit(true); setTimeout(() => onDismiss(t.id), 280); }, [t.id, onDismiss]);
    useEffect(() => {
        if (t.persistent) return;
        const tick = () => { const p = Math.max(0, 100 - ((Date.now() - ts.current) / TOAST_MS) * 100); setProg(p); if (p > 0) raf.current = requestAnimationFrame(tick); else dismiss(); };
        raf.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf.current);
    }, [t.persistent, dismiss]);
    return (
        <div style={{ position: "relative", overflow: "hidden", background: "#fff", borderRadius: 10, borderLeft: `4px solid ${m.bar}`, boxShadow: "0 4px 20px rgba(0,0,0,.1)", minWidth: 260, maxWidth: 340, animation: exit ? "vdOut .28s ease forwards" : "vdIn .28s cubic-bezier(.34,1.56,.64,1) forwards" }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 13px 13px" }}>
                <div style={{ width: 22, height: 22, borderRadius: "50%", background: `${m.bar}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    {t.type === "loading"
                        ? <svg width="12" height="12" viewBox="0 0 13 13" fill="none" style={{ animation: "vdSpin .85s linear infinite" }}><circle cx="6.5" cy="6.5" r="5" stroke={m.bar} strokeWidth="2" strokeDasharray="8 20" strokeLinecap="round" /></svg>
                        : <span style={{ fontSize: 11, fontWeight: 800, color: m.bar }}>{m.icon}</span>}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 650, color: "#0f172a" }}>{t.title}</p>
                    {t.msg && <p style={{ margin: "3px 0 0", fontSize: 12, color: "#64748b" }}>{t.msg}</p>}
                </div>
                <button onClick={dismiss} style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", padding: 2, display: "flex" }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
            </div>
            {!t.persistent && (
                <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: "#f1f5f9" }}>
                    <div style={{ height: "100%", width: `${prog}%`, background: m.bar, transition: "width .1s linear" }} />
                </div>
            )}
        </div>
    );
};
const ToastContainer = () => {
    const [toasts, setToasts] = useState([]);
    _vdSetter = setToasts;
    const dismiss = useCallback((id) => setToasts((p) => p.filter((t) => t.id !== id)), []);
    if (!toasts.length) return null;
    return (
        <div style={{ position: "fixed", bottom: 24, right: 16, zIndex: 9999, display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
            {toasts.map((t) => <TItem key={t.id} t={t} onDismiss={dismiss} />)}
        </div>
    );
};

const I = {
    plus:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>,
    edit:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
    trash:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14H6L5 6" /><path d="M10 11v6M14 11v6" /><path d="M9 6V4h6v2" /></svg>,
    close:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
    search:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
    file:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
    download: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
    link:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>,
    upload:   <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>,
    refresh:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" /></svg>,
    spinner:  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: "vdSpin .8s linear infinite" }}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" /></svg>,
    eye:      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
    image:    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>,
};

const Shimmer = ({ w, h = 14 }) => (
    <div style={{ height: h, width: w || "100%", borderRadius: 6, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "vdShimmer 1.4s infinite" }} />
);

const Btn = ({ children, onClick, variant = "primary", size = "sm", disabled, loading, icon, style: extraStyle }) => {
    const S = {
        primary:   { bg: "#3b82f6", color: "#fff",     border: "none",               hov: "#2563eb" },
        secondary: { bg: "#fff",    color: "#374151",   border: "1px solid #e2e8f0",  hov: "#f8fafc" },
        danger:    { bg: "#fff1f2", color: "#f43f5e",   border: "1px solid #fecdd3",  hov: "#ffe4e6" },
        ghost:     { bg: "none",    color: "#64748b",   border: "none",               hov: "#f1f5f9" },
        success:   { bg: "#f0fdf4", color: "#16a34a",   border: "1px solid #bbf7d0",  hov: "#dcfce7" },
    };
    const s = S[variant] || S.primary;
    const pad = size === "sm" ? "6px 13px" : "9px 20px";
    return (
        <button onClick={onClick} disabled={disabled || loading}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: pad, background: s.bg, color: s.color, border: s.border, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: (disabled || loading) ? "not-allowed" : "pointer", opacity: (disabled || loading) ? 0.6 : 1, transition: "background .15s", whiteSpace: "nowrap", ...extraStyle }}
            onMouseEnter={(e) => { if (!disabled && !loading) e.currentTarget.style.background = s.hov; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = s.bg; }}>
            {loading ? I.spinner : icon}{children}
        </button>
    );
};

const SectionHead = ({ icon, title, count, action }) => (
    <div className="vd-section-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <span style={{ color: "#f59e0b", display: "flex", flexShrink: 0 }}>{icon}</span>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#0f172a" }}>{title}</h3>
            {count != null && (
                <span style={{ fontSize: 11.5, background: "#fef3c7", color: "#d97706", padding: "1px 8px", borderRadius: 20, fontWeight: 700, border: "1px solid #fde68a", flexShrink: 0 }}>{count}</span>
            )}
        </div>
        {action && <div className="vd-actions" style={{ display: "flex", gap: 8, flexShrink: 0 }}>{action}</div>}
    </div>
);

const Modal = ({ title, sub, onClose, children, width = 500 }) => {
    useEffect(() => {
        const h = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", h);
        return () => document.removeEventListener("keydown", h);
    }, [onClose]);
    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.52)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: "16px" }}
            onClick={onClose}>
            <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: width, boxShadow: "0 24px 64px rgba(0,0,0,.18)", animation: "vdFadeUp .2s ease", maxHeight: "95vh", display: "flex", flexDirection: "column" }}
                onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "18px 22px 0", flexShrink: 0 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</h3>
                        {sub && <p style={{ margin: "3px 0 0", fontSize: 12, color: "#94a3b8" }}>{sub}</p>}
                    </div>
                    <button onClick={onClose}
                        style={{ background: "#f1f5f9", border: "none", borderRadius: 7, cursor: "pointer", padding: 7, display: "flex", color: "#64748b", flexShrink: 0, marginLeft: 12 }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#e2e8f0"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#f1f5f9"}>
                        {I.close}
                    </button>
                </div>
                <div style={{ padding: "18px 22px 22px", overflowY: "auto", flex: 1 }}>{children}</div>
            </div>
        </div>
    );
};

const ConfirmDelete = ({ docName, onConfirm, onCancel, busy }) => (
    <Modal title="Delete Document" onClose={onCancel} width={400}>
        <div style={{ textAlign: "center", paddingTop: 8 }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#fff5f5", border: "2px solid #fecaca", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", fontSize: 24 }}>🗑️</div>
            <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Remove this document?</p>
            <p style={{ margin: "0 0 22px", fontSize: 13, color: "#64748b", lineHeight: 1.6 }}>
                <strong style={{ color: "#374151" }}>{docName}</strong> will be permanently deleted and cannot be recovered.
            </p>
            <div style={{ display: "flex", justifyContent: "center", gap: 10 }}>
                <Btn variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Btn>
                <Btn variant="danger" onClick={onConfirm} loading={busy}>Delete</Btn>
            </div>
        </div>
    </Modal>
);

const DocPreviewModal = ({ doc, onClose }) => {
    const kind = getFileKind(doc);
    const cfg  = getDocCfg(doc.doc_type);
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async () => {
        setDownloading(true);
        await downloadFile(doc.file_url, doc.original_name);
        setDownloading(false);
    };

    return (
        <Modal
            title={doc.original_name}
            sub={`${cfg.emoji} ${cfg.label} · ${formatBytes(doc.size)} · ${doc.uploaded_at}`}
            onClose={onClose}
            width={900}
        >
            <div style={{ borderRadius: 10, overflow: "hidden", background: kind === "image" ? "#0f172a" : "#f8fafc", border: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 360 }}>
                {kind === "image" && (
                    <img
                        src={doc.file_url}
                        alt={doc.original_name}
                        style={{ maxWidth: "100%", maxHeight: "65vh", objectFit: "contain", display: "block" }}
                        onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
                    />
                )}
                {kind === "image" && (
                    <div style={{ display: "none", flexDirection: "column", alignItems: "center", gap: 8, color: "#64748b", padding: 40 }}>
                        <span style={{ fontSize: 48 }}>🖼️</span>
                        <span style={{ fontSize: 13 }}>Image could not be loaded</span>
                    </div>
                )}

                {kind === "pdf" && (
                    <iframe
                        src={doc.file_url}
                        title={doc.original_name}
                        style={{ width: "100%", height: "70vh", border: "none", display: "block", minHeight: 400 }}
                    />
                )}

                {(kind === "spreadsheet" || kind === "word" || kind === "other") && (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "60px 40px", textAlign: "center" }}>
                        <span style={{ fontSize: 64, lineHeight: 1 }}>{FILE_KIND_META[kind].icon}</span>
                        <div>
                            <p style={{ margin: "0 0 6px", fontSize: 15, fontWeight: 700, color: "#0f172a" }}>{doc.original_name}</p>
                            <p style={{ margin: 0, fontSize: 13, color: "#64748b" }}>
                                {FILE_KIND_META[kind].label} · {formatBytes(doc.size)}
                            </p>
                        </div>
                        <p style={{ margin: 0, fontSize: 13, color: "#94a3b8", background: "#f1f5f9", padding: "8px 16px", borderRadius: 8 }}>
                            Browser preview is not available for this file type. Use download to open it.
                        </p>
                    </div>
                )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                <Btn variant="secondary" onClick={handleDownload} loading={downloading} icon={I.download}>
                    Download
                </Btn>
                <a
                    href={doc.file_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 14px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, color: "#2563eb", fontSize: 13, fontWeight: 600, textDecoration: "none" }}
                >
                    {I.link} Open in New Tab
                </a>
            </div>
        </Modal>
    );
};

const DocFormModal = ({ vendorProfileId, editDoc, onClose, onSaved }) => {
    const isEdit = !!editDoc;
    const [docType, setDocType] = useState(editDoc?.doc_type || "gstin");
    const [file,    setFile]    = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [busy,    setBusy]    = useState(false);
    const [errors,  setErrors]  = useState({});
    const fileRef = useRef(null);

    const validate = () => {
        const e = {};
        if (!docType) e.docType = "Please select a document type.";
        if (!isEdit && !file) e.file = "Please select a file to upload.";
        return e;
    };

    const handleFile = (f) => {
        if (!f) return;
        if (f.size > 10 * 1024 * 1024) { setErrors((p) => ({ ...p, file: "File size must be under 10 MB." })); return; }
        setFile(f);
        setErrors((p) => { const n = { ...p }; delete n.file; return n; });
    };

    const handleSubmit = async () => {
        const e = validate();
        if (Object.keys(e).length) { setErrors(e); return; }
        setBusy(true);
        const tid = toast.loading(isEdit ? "Updating document type…" : "Uploading document…");
        try {
            let data;
            if (isEdit) {
                const { data: resData } = await docApi.patch(vendorProfileId, editDoc.id, { doc_type: docType });
                data = resData;
            } else {
                const fd = new FormData();
                fd.append("doc_type", docType);
                fd.append("file", file);
                const { data: resData } = await docApi.create(vendorProfileId, fd);
                data = resData;
            }
            toast.dismiss(tid);
            if (data?.status >= 400) {
                const apiErrors = {};
                if (data?.data?.doc_type) apiErrors.docType = Array.isArray(data.data.doc_type) ? data.data.doc_type[0] : data.data.doc_type;
                if (data?.data?.file) apiErrors.file = Array.isArray(data.data.file) ? data.data.file[0] : data.data.file;
                if (data?.data?.detail) apiErrors._global = data.data.detail;
                if (data?.data?.non_field_errors) apiErrors._global = Array.isArray(data.data.non_field_errors) ? data.data.non_field_errors[0] : data.data.non_field_errors;
                if (Object.keys(apiErrors).length) { setErrors(apiErrors); } else { toast.error("Upload Failed", `Server error ${data?.status}.`); }
                return;
            }
            toast.success(isEdit ? "Doc Type Updated" : "Document Uploaded", isEdit ? "Document type has been changed." : `${file?.name || "Document"} uploaded successfully.`);
            onSaved(data?.data ?? data);
        } catch (err) {
            toast.dismiss(tid);
            toast.error(err?.name === "TypeError" ? "Network Error" : "Unexpected Error", err?.name === "TypeError" ? "Unable to reach the server." : "Something went wrong.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Modal title={isEdit ? "Change Document Type" : "Upload New Document"} sub={isEdit ? `Editing: ${editDoc.original_name}` : "Add document for this vendor"} onClose={onClose} width={500}>
            {errors._global && (
                <div style={{ marginBottom: 14, padding: "10px 14px", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 8, color: "#dc2626", fontSize: 13 }}>
                    ⚠ {errors._global}
                </div>
            )}

            <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: errors.docType ? "#dc2626" : "#374151", display: "block", marginBottom: 8 }}>
                    Document Type <span style={{ color: "#f43f5e" }}>*</span>
                </label>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
                    {DOC_TYPES.map((dt) => (
                        <button key={dt.value} onClick={() => { setDocType(dt.value); setErrors((p) => { const n = { ...p }; delete n.docType; return n; }); }}
                            style={{ padding: "10px 8px", borderRadius: 9, border: `2px solid ${docType === dt.value ? dt.color : "#e2e8f0"}`, background: docType === dt.value ? dt.bg : "#fff", cursor: "pointer", transition: "all .15s", textAlign: "center" }}>
                            <div style={{ fontSize: 18, marginBottom: 3 }}>{dt.emoji}</div>
                            <div style={{ fontSize: 11.5, fontWeight: 700, color: docType === dt.value ? dt.color : "#64748b" }}>{dt.label}</div>
                        </button>
                    ))}
                </div>
                {errors.docType && <p style={{ margin: "6px 0 0", fontSize: 12, color: "#dc2626" }}>⚠ {errors.docType}</p>}
            </div>

            {!isEdit && (
                <div style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: 13, fontWeight: 600, color: errors.file ? "#dc2626" : "#374151", display: "block", marginBottom: 8 }}>
                        File <span style={{ color: "#f43f5e" }}>*</span>
                    </label>
                    <div
                        onClick={() => fileRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
                        style={{ border: `2px dashed ${errors.file ? "#f43f5e" : dragOver ? "#3b82f6" : "#cbd5e1"}`, borderRadius: 10, padding: "24px 20px", textAlign: "center", cursor: "pointer", background: dragOver ? "#eff6ff" : errors.file ? "#fff5f5" : "#f8fafc", transition: "all .15s" }}>
                        <input ref={fileRef} type="file" accept="*/*" style={{ display: "none" }} onChange={(e) => handleFile(e.target.files[0])} />
                        {file ? (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                                <div style={{ width: 42, height: 42, borderRadius: 10, background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                                    {file.type.startsWith("image/") ? "🖼️" : file.type.includes("pdf") ? "📄" : "📁"}
                                </div>
                                <span style={{ fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>{file.name}</span>
                                <span style={{ fontSize: 12, color: "#64748b" }}>{formatBytes(file.size)}</span>
                                <span style={{ fontSize: 11.5, color: "#3b82f6", fontWeight: 600 }}>Click to change file</span>
                            </div>
                        ) : (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                                <div style={{ color: "#94a3b8", marginBottom: 4 }}>{I.upload}</div>
                                <span style={{ fontSize: 13.5, fontWeight: 600, color: "#374151" }}>Drop file here or click to browse</span>
                                <span style={{ fontSize: 12, color: "#94a3b8" }}>Images, PDF, Excel, Word — max 10 MB</span>
                            </div>
                        )}
                    </div>
                    {errors.file && <p style={{ margin: "6px 0 0", fontSize: 12, color: "#dc2626" }}>⚠ {errors.file}</p>}
                </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
                <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
                <Btn variant="primary" onClick={handleSubmit} loading={busy} icon={isEdit ? I.edit : I.upload}>
                    {isEdit ? "Update Type" : "Upload Document"}
                </Btn>
            </div>
        </Modal>
    );
};

const DocCardSkeleton = () => (
    <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
        <div style={{ height: 130, background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)", backgroundSize: "200% 100%", animation: "vdShimmer 1.4s infinite" }} />
        <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
            <Shimmer w="55%" h={18} /><Shimmer w="80%" h={13} /><Shimmer w="40%" h={12} />
        </div>
    </div>
);

const DocCard = ({ doc, onEdit, onDelete, onPreview }) => {
    const cfg      = getDocCfg(doc.doc_type);
    const kind     = getFileKind(doc);
    const kindMeta = FILE_KIND_META[kind];
    const [imgErr, setImgErr]         = useState(false);
    const [downloading, setDownloading] = useState(false);

    const handleDownload = async (e) => {
        e.stopPropagation();
        setDownloading(true);
        await downloadFile(doc.file_url, doc.original_name);
        setDownloading(false);
    };

    const ThumbnailContent = () => {
        if (kind === "image" && !imgErr) {
            return (
                <>
                    <img
                        src={doc.file_url}
                        alt={doc.original_name}
                        style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.9 }}
                        onError={() => setImgErr(true)}
                    />
                    <button
                        onClick={() => onPreview(doc)}
                        style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background .2s" }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,0,0,.45)"; e.currentTarget.querySelector(".vd-eye").style.opacity = 1; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,0,0,0)"; e.currentTarget.querySelector(".vd-eye").style.opacity = 0; }}
                    >
                        <span className="vd-eye" style={{ opacity: 0, transition: "opacity .2s", color: "#fff", display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, background: "rgba(0,0,0,.5)", padding: "6px 14px", borderRadius: 8 }}>
                            {I.eye} Preview
                        </span>
                    </button>
                </>
            );
        }

        return (
            <button
                onClick={() => onPreview(doc)}
                style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 8, background: kindMeta.bg, border: "none", cursor: "pointer", transition: "filter .2s" }}
                onMouseEnter={(e) => e.currentTarget.style.filter = "brightness(.95)"}
                onMouseLeave={(e) => e.currentTarget.style.filter = "none"}
            >
                <span style={{ fontSize: 38, lineHeight: 1 }}>{kindMeta.icon}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: kindMeta.color || cfg.color, display: "flex", alignItems: "center", gap: 5 }}>
                    {I.eye} {kindMeta.label}
                </span>
            </button>
        );
    };

    return (
        <div
            style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)", transition: "box-shadow .2s, transform .2s", display: "flex", flexDirection: "column" }}
            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,.08)"; e.currentTarget.style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,.04)"; e.currentTarget.style.transform = "none"; }}
        >
            <div style={{ height: 136, overflow: "hidden", position: "relative", flexShrink: 0 }}>
                <ThumbnailContent />
                <span style={{ position: "absolute", top: 8, left: 8, padding: "3px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`, lineHeight: 1.5, pointerEvents: "none" }}>
                    {cfg.emoji} {cfg.label}
                </span>
            </div>

            <div style={{ padding: "12px 14px 14px", flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={doc.original_name}>
                    {doc.original_name}
                </p>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{formatBytes(doc.size)}</span>
                    <span style={{ fontSize: 11.5, color: "#94a3b8" }}>{doc.uploaded_at}</span>
                </div>

                <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                    <button
                        onClick={handleDownload}
                        disabled={downloading}
                        style={{ flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "6px 10px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 7, color: "#2563eb", fontSize: 12, fontWeight: 600, cursor: downloading ? "not-allowed" : "pointer", transition: "background .15s", opacity: downloading ? 0.7 : 1 }}
                        onMouseEnter={(e) => { if (!downloading) e.currentTarget.style.background = "#dbeafe"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "#eff6ff"; }}
                    >
                        {downloading ? I.spinner : I.download} {downloading ? "…" : "Download"}
                    </button>

                    <button onClick={() => onEdit(doc)} title="Change doc type"
                        style={{ padding: "6px 10px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 7, cursor: "pointer", color: "#64748b", display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, transition: "background .15s" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#f1f5f9"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#f8fafc"}>
                        {I.edit}
                    </button>
                    <button onClick={() => onDelete(doc)} title="Delete document"
                        style={{ padding: "6px 10px", background: "#fff5f5", border: "1px solid #fecdd3", borderRadius: 7, cursor: "pointer", color: "#f43f5e", display: "flex", alignItems: "center", gap: 4, fontSize: 12, fontWeight: 600, transition: "background .15s" }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#ffe4e6"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "#fff5f5"}>
                        {I.trash}
                    </button>
                </div>
            </div>
        </div>
    );
};

const VendorDocuments = ({ vendorProfileId }) => {
    const [docs,        setDocs]        = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [fetchError,  setFetchError]  = useState(null);
    const [search,      setSearch]      = useState("");
    const [debouncedQ,  setDebouncedQ]  = useState("");

    const [showUpload,  setShowUpload]  = useState(false);
    const [editDoc,     setEditDoc]     = useState(null);
    const [deleteDoc,   setDeleteDoc]   = useState(null);
    const [previewDoc,  setPreviewDoc]  = useState(null);
    const [deleteBusy,  setDeleteBusy]  = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedQ(search), 380);
        return () => clearTimeout(t);
    }, [search]);

    const fetchDocs = useCallback(async (q = debouncedQ) => {
        if (!vendorProfileId) return;
        setLoading(true);
        setFetchError(null);
        try {
            const { data } = await docApi.list(vendorProfileId, q);
            if (data?.status === 401) { setFetchError("Session expired. Please log in again."); return; }
            if (data?.status === 403) { setFetchError("You don't have permission to view documents."); return; }
            if (data?.status >= 400) throw new Error("Load failed");
            if (data?.status === "success" || Array.isArray(data?.results)) {
                setDocs(data?.results ?? []);
            } else {
                setFetchError("Unexpected response from server.");
            }
        } catch (err) {
            const msg = err?.name === "TypeError"
                ? "Unable to reach the server. Check your connection."
                : `Failed to load documents (${err.message}).`;
            setFetchError(msg);
            toast.error("Load Failed", msg);
        } finally {
            setLoading(false);
        }
    }, [vendorProfileId, debouncedQ]);

    useEffect(() => { fetchDocs(); }, [fetchDocs]);

    const handleSaved = (savedDoc) => {
        if (editDoc) {
            setDocs((prev) => prev.map((d) => (d.id === savedDoc.id ? savedDoc : d)));
        } else {
            setDocs((prev) => [savedDoc, ...prev]);
        }
        setShowUpload(false);
        setEditDoc(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteDoc) return;
        setDeleteBusy(true);
        const tid = toast.loading("Deleting document…");
        try {
            const { data } = await docApi.delete(vendorProfileId, deleteDoc.id);
            toast.dismiss(tid);
            if (data?.status === 204 || data?.status === 200 || data?.status === 404) {
                setDocs((prev) => prev.filter((d) => d.id !== deleteDoc.id));
                toast.success("Document Deleted", `${deleteDoc.original_name} has been removed.`);
                setDeleteDoc(null);
            } else if (data?.status === 401) {
                toast.error("Unauthorized", "Your session has expired.");
            } else if (data?.status === 403) {
                toast.error("Forbidden", "You don't have permission to delete this.");
            } else if (data?.status >= 400) {
                toast.error("Delete Failed", data?.data?.detail || `Server error ${data?.status}.`);
            }
        } catch (err) {
            toast.dismiss(tid);
            toast.error(err?.name === "TypeError" ? "Network Error" : "Unexpected Error", err?.name === "TypeError" ? "Unable to reach the server." : "Something went wrong.");
        } finally {
            setDeleteBusy(false);
        }
    };

    const typeCounts = DOC_TYPES.reduce((acc, dt) => {
        acc[dt.value] = docs.filter((d) => d.doc_type === dt.value).length;
        return acc;
    }, {});

    return (
        <>
            <style>{`
        @keyframes vdShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes vdFadeUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:none} }
        @keyframes vdIn      { from{opacity:0;transform:translateX(20px) scale(.97)} to{opacity:1;transform:none} }
        @keyframes vdOut     { from{opacity:1} to{opacity:0;transform:translateX(24px) scale(.95)} }
        @keyframes vdSpin    { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        * { box-sizing: border-box; }

        /* Responsive: section header stacks on mobile */
        @media (max-width: 540px) {
          .vd-section-head { flex-direction: column !important; align-items: flex-start !important; }
          .vd-actions { width: 100%; justify-content: flex-end; }
        }

        /* Responsive: type badges wrap on small screens */
        @media (max-width: 400px) {
          .vd-type-badges { gap: 6px !important; }
        }

        /* Preview modal: full-height iframe on mobile */
        @media (max-width: 640px) {
          .vd-preview-frame { height: 55vh !important; min-height: 260px !important; }
        }
      `}</style>

            <ToastContainer />

            <SectionHead
                icon={I.file}
                title="Documents"
                count={loading ? null : docs.length}
                action={
                    <>
                        <Btn variant="secondary" onClick={() => fetchDocs()} icon={I.refresh} loading={loading}>Refresh</Btn>
                        <Btn variant="primary" onClick={() => { setEditDoc(null); setShowUpload(true); }} icon={I.plus}>Upload</Btn>
                    </>
                }
            />

            {!loading && docs.length > 0 && (
                <div className="vd-type-badges" style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap", animation: "vdFadeUp .2s ease" }}>
                    {DOC_TYPES.map((dt) => typeCounts[dt.value] > 0 && (
                        <div key={dt.value} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 12px", background: dt.bg, border: `1px solid ${dt.border}`, borderRadius: 20 }}>
                            <span style={{ fontSize: 13 }}>{dt.emoji}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: dt.color }}>{dt.label}</span>
                            <span style={{ fontSize: 12, fontWeight: 700, color: dt.color, background: "rgba(255,255,255,.5)", padding: "1px 6px", borderRadius: 10 }}>{typeCounts[dt.value]}</span>
                        </div>
                    ))}
                </div>
            )}

            <div style={{ position: "relative", marginBottom: 18 }}>
                <span style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", display: "flex", pointerEvents: "none" }}>{I.search}</span>
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search documents by name or type…"
                    style={{ width: "100%", padding: "9px 36px 9px 34px", border: "1px solid #e2e8f0", borderRadius: 9, fontSize: 13.5, background: "#f8fafc", color: "#1e293b", outline: "none" }}
                    onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; e.target.style.background = "#fff"; }}
                    onBlur={(e)  => { e.target.style.borderColor = "#e2e8f0"; e.target.style.background = "#f8fafc"; }}
                />
                {search && (
                    <button onClick={() => setSearch("")}
                        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 3 }}>
                        {I.close}
                    </button>
                )}
            </div>

            {fetchError && !loading && (
                <div style={{ padding: "14px 16px", background: "#fff5f5", border: "1px solid #fecaca", borderRadius: 10, color: "#dc2626", fontSize: 13, marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <span>⚠ {fetchError}</span>
                    <Btn variant="secondary" onClick={() => fetchDocs()} size="sm">Retry</Btn>
                </div>
            )}

            {loading && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14 }}>
                    {[1,2,3,4,5,6].map((i) => <DocCardSkeleton key={i} />)}
                </div>
            )}

            {!loading && !fetchError && docs.length === 0 && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 24px", gap: 12, textAlign: "center", background: "#fff", borderRadius: 14, border: "1.5px dashed #e2e8f0", animation: "vdFadeUp .22s ease" }}>
                    <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#0f172a" }}>
                        {search ? "No documents match your search" : "No Documents Yet"}
                    </p>
                    <p style={{ margin: 0, fontSize: 13, color: "#94a3b8", maxWidth: 300, lineHeight: 1.7 }}>
                        {search ? "Try searching with a different term." : "Upload GSTIN, PAN, COI, or MSME for this vendor."}
                    </p>
                    {search
                        ? <Btn variant="secondary" onClick={() => setSearch("")} icon={I.close}>Clear Search</Btn>
                        : <Btn variant="primary"   onClick={() => setShowUpload(true)} icon={I.plus}>Upload First Document</Btn>}
                </div>
            )}

            {!loading && !fetchError && docs.length > 0 && (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 14, animation: "vdFadeUp .2s ease" }}>
                    {docs.map((doc) => (
                        <DocCard
                            key={doc.id}
                            doc={doc}
                            onEdit={(d)    => { setEditDoc(d); setShowUpload(true); }}
                            onDelete={(d)  => setDeleteDoc(d)}
                            onPreview={(d) => setPreviewDoc(d)}
                        />
                    ))}
                </div>
            )}

            {showUpload && (
                <DocFormModal
                    vendorProfileId={vendorProfileId}
                    editDoc={editDoc}
                    onClose={() => { setShowUpload(false); setEditDoc(null); }}
                    onSaved={handleSaved}
                />
            )}
            {deleteDoc && (
                <ConfirmDelete
                    docName={deleteDoc.original_name}
                    onConfirm={handleDeleteConfirm}
                    onCancel={() => setDeleteDoc(null)}
                    busy={deleteBusy}
                />
            )}
            {previewDoc && (
                <DocPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
            )}
        </>
    );
};

export default VendorDocuments;